import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useContext, useEffect, useRef, type ReactNode } from 'react';
import { ApiError, authApi, type Session } from '../lib/api';
import { forgetInvoiceDismissals } from '../features/billing/invoices';
import { forgetPendingFlows } from '../features/posts/pendingFlows';

interface SessionState {
  session: Session | null;
  isLoading: boolean;
  refresh: () => Promise<void>;
}

const SessionContext = createContext<SessionState | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['session'],
    queryFn: async () => {
      try {
        return await authApi.me();
      } catch (err) {
        // 401 is the normal signed-out state, not a failure worth retrying.
        if (err instanceof ApiError && err.status === 401) return null;
        throw err;
      }
    },
    retry: false,
    staleTime: 30_000,
  });

  // A session that ends — Sign out, or one that expired while the tab was open —
  // leaves nothing of the user in this browser: the post flows kept for them
  // and the notices they dismissed (D-134) (the notice itself went with D-186).
  const lastUserId = useRef<string | null>(null);
  useEffect(() => {
    if (data) {
      lastUserId.current = data.user.id;
    } else if (data === null && lastUserId.current) {
      forgetPendingFlows(lastUserId.current);
      forgetInvoiceDismissals();
      lastUserId.current = null;
    }
  }, [data]);

  const value: SessionState = {
    session: data ?? null,
    isLoading,
    refresh: async () => {
      await queryClient.invalidateQueries({ queryKey: ['session'] });
    },
  };

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionState {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession must be used inside <SessionProvider>');
  return ctx;
}
